import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Link } from 'react-router-dom';
import { subDays } from 'date-fns';
import { COBStep } from '~/client-flow/clientOb/ClientOnboarding.utils';
import { routes } from '~/constants';
import { Intent } from '~/infra/access-control';
import { relationshipSummaryModel, timeModel } from '~/models';
import { RelationshipProgressStatus } from '~/models/relationship';
import { fromDateStampObject } from '~/models/time';
import { formatShortMonthUSDate } from '~/utils';
export const proposalsDashboardTextsPath = 'pages.proposalsDashboard';
const fiveDays = 1000 * 60 * 60 * 24 * 5;
const tenDays = fiveDays * 2;
const didTimePassed = (timestamp, time)=>{
    const timePassed = Date.now() - timestamp;
    return timePassed > time;
};
const statusToSeverity = {
    withdrawnForEditing: 11,
    microDepositStarted: 10,
    microDepositFailed: 9,
    paymentMethod: 8,
    reviewed: 7,
    sent: 6,
    expired: 5
};
export const proposalStatusDefinition = (isVendor, texts)=>{
    return {
        withdrawnForEditing: {
            severity: statusToSeverity.withdrawnForEditing,
            statusText: texts.statusLabels.pendingRepublishing(),
            statusSkin: 'standard'
        },
        sent: {
            severity: statusToSeverity.sent,
            statusText: isVendor ? texts.statusLabels.proposalSentToClient() : texts.statusLabels.sent(),
            statusSkin: 'standard'
        },
        reviewed: {
            severity: statusToSeverity.reviewed,
            statusText: isVendor ? texts.statusLabels.reviewedByClient() : texts.statusLabels.reviewed(),
            statusSkin: 'standard'
        },
        microDepositFailed: {
            severity: statusToSeverity.microDepositFailed,
            statusText: texts.statusLabels.microDepositFailed(),
            statusSkin: 'error'
        },
        microDepositStarted: {
            severity: statusToSeverity.microDepositStarted,
            statusText: texts.statusLabels.initiatedMicroDeposit(),
            statusSkin: 'standard'
        },
        paymentMethod: {
            severity: statusToSeverity.paymentMethod,
            statusText: texts.statusLabels.connectedPaymentMethod(),
            statusSkin: 'standard'
        },
        expired: {
            severity: statusToSeverity.expired,
            statusText: texts.statusLabels.expired(),
            statusSkin: 'standard'
        }
    };
};
const isStatusMicroDepositFailed = (agreement)=>(agreement === null || agreement === void 0 ? void 0 : agreement.progressStatus) === RelationshipProgressStatus.MicroDeposit && didTimePassed(agreement === null || agreement === void 0 ? void 0 : agreement.progressStatusTimestamp, tenDays);
export const getAgreementStatus = (agreement)=>{
    const { progressStatus, progressStatusTimestamp } = agreement;
    const shouldOverrideOriginalStatus = isStatusMicroDepositFailed(agreement);
    if (shouldOverrideOriginalStatus) {
        const mdDate = new Date(progressStatusTimestamp);
        mdDate.setDate(mdDate.getDate() + 10);
        const newTimestamp = mdDate.getTime();
        return {
            progressStatus: RelationshipProgressStatus.MicroDepositFailed,
            progressStatusTimestamp: newTimestamp
        };
    }
    return {
        progressStatus,
        progressStatusTimestamp
    };
};
export const getSentByUIData = (agreement)=>{
    const { seller } = agreement;
    const sellerDisplayValue = "".concat(seller.firstName, " ").concat(seller.lastName);
    return sellerDisplayValue;
};
export const getStatusUIData = (agreement, isVendor, texts)=>{
    const { progressStatusTimestamp, expirationDate } = agreement;
    const statusDefinition = proposalStatusDefinition(isVendor, texts);
    if (agreement.withdrawnForEditing) {
        const lastUpdateText = progressStatusTimestamp ? timeModel.formatTimeAgo(progressStatusTimestamp) : '';
        return _object_spread_props(_object_spread({}, statusDefinition.withdrawnForEditing), {
            lastUpdateText,
            viewableStatus: 'withdrawnForEditing'
        });
    }
    const progressStatus = agreement.progressStatus;
    let lastUpdateText = expirationDate ? texts.statusLabels.validUntil({
        date: formatShortMonthUSDate(subDays(fromDateStampObject(expirationDate), 1))
    }) : progressStatusTimestamp ? timeModel.formatTimeAgo(progressStatusTimestamp) : '';
    let viewableStatus;
    switch(progressStatus){
        case RelationshipProgressStatus.MicroDeposit:
            {
                viewableStatus = isStatusMicroDepositFailed(agreement) ? 'microDepositFailed' : 'microDepositStarted';
                lastUpdateText = viewableStatus === 'microDepositFailed' && progressStatusTimestamp ? timeModel.formatTimeAgo(progressStatusTimestamp + tenDays) : lastUpdateText;
                break;
            }
        case RelationshipProgressStatus.Expired:
            {
                viewableStatus = progressStatus;
                lastUpdateText = texts.statusLabels.expiredOn({
                    date: formatShortMonthUSDate(fromDateStampObject(expirationDate))
                });
                break;
            }
        default:
            viewableStatus = progressStatus;
            break;
    }
    return _object_spread_props(_object_spread({}, statusDefinition[viewableStatus]), {
        lastUpdateText,
        viewableStatus
    });
};
const sortByCompanyName = function sortByCompanyName() {
    let isVendor = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : false;
    return (a, b)=>{
        const nameA = relationshipSummaryModel.opposingPartyName(a, isVendor).toLowerCase();
        const nameB = relationshipSummaryModel.opposingPartyName(b, isVendor).toLowerCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    };
};
const sortById = (a, b)=>a.id < b.id ? -1 : 1;
const sortBySentDate = (a, b)=>b.createdOn.getTime() - a.createdOn.getTime();
const sortBySentBy = (a, b)=>{
    const sellerA = getSentByUIData(a);
    const sellerB = getSentByUIData(b);
    if (sellerA === sellerB) {
        return 0;
    } else if (sellerA > sellerB) {
        return 1;
    } else {
        return -1;
    }
};
const sortByStatus = (a, b, isVendor, texts)=>{
    const { severity: severityA } = getStatusUIData(a, isVendor, texts);
    const { severity: severityB } = getStatusUIData(b, isVendor, texts);
    if (severityA < severityB) {
        return -1;
    }
    if (severityA > severityB) {
        return 1;
    }
    return 0;
};
const sortByStatusUpdate = (a, b)=>{
    if (!a.progressStatusTimestamp && !b.progressStatusTimestamp) {
        return 0;
    }
    if (!a.progressStatusTimestamp) {
        return 1;
    }
    if (!b.progressStatusTimestamp) {
        return -1;
    }
    return b.progressStatusTimestamp - a.progressStatusTimestamp;
};
export const sortFunctions = {
    0: (param)=>{
        let { isAscending, isVendor } = param;
        return (a, b)=>{
            const retval = sortByCompanyName(isVendor)(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    1: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortBySentDate(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    2: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const sentBySort = sortBySentBy(a, b);
            return isAscending ? -sentBySort : sentBySort;
        };
    },
    3: (param)=>{
        let { isAscending, isVendor, texts } = param;
        return (a, b)=>{
            const statusSort = sortByStatus(a, b, isVendor, texts);
            return (isAscending ? -statusSort : statusSort) || sortByStatusUpdate(a, b) || sortById(a, b);
        };
    },
    4: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByStatusUpdate(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    }
};
export function getProposalsStatusCounters(agreements, isVendor, texts) {
    return agreements.reduce((acc, agreement)=>{
        const statusUI = getStatusUIData(agreement, isVendor, texts);
        acc[statusUI.viewableStatus] = (acc[statusUI.viewableStatus] || 0) + 1;
        return acc;
    }, {});
}
export function getRowAndMainAction(param) {
    let { proposal, isVendor, isViewMode, heapId, history, openClientPreview, texts } = param;
    if (isVendor || isViewMode) {
        return {
            rowProps: {
                as: 'button',
                onClick: openClientPreview
            },
            mainAction: {
                buttonText: isViewMode ? texts.mainAction.view() : texts.mainAction.preview(),
                intent: isViewMode ? Intent.ClientViewProposal : undefined,
                onClickButton: openClientPreview
            }
        };
    } else {
        const clientAgreementLink = routes.clientOnboarding(proposal.id, isStatusMicroDepositFailed(proposal) ? COBStep.paymentMethod : undefined);
        return {
            rowProps: {
                as: Link,
                to: clientAgreementLink
            },
            mainAction: {
                buttonText: texts.mainAction.view(),
                intent: Intent.ClientViewProposal,
                btnHeapId: "".concat(heapId, "-view-btn"),
                onClickButton: ()=>history.push(clientAgreementLink)
            }
        };
    }
}
